<template>
  <div id="universityCampaigns">
    <div class="sub-title">
      <span class="fs-24">{{ $utils("addCommasInNumber", campaignsCount) }}</span>
      {{ $t("happilyVillage.campaigns") }}
    </div>
    
    <div class="flex-row-between mt-14">
      <div class="flex-center" style="gap: 12px;">
        <div class="relative">
          <el-input
            class="input-style"
            style="height: 40px; width: 300px;"
            :placeholder="$t('happilyVillage.Search')"
            @keyup.native="handleSearch"
            v-model="searchQuery"
            clearable
          ></el-input>
        </div>
        <el-select
          v-model="statusFilter"
          :placeholder="$t('happilyVillage.All')"
          class="input-style"
          style="width: 180px;"
          @change="handleFilterChange"
        >
          <el-option :label="$t('happilyVillage.All')" value="all"></el-option>
          <el-option :label="$t('happilyVillage.status_active')" value="active"></el-option>
          <el-option :label="$t('happilyVillage.status_scheduled')" value="scheduled"></el-option>
          <el-option :label="$t('happilyVillage.status_ended')" value="ended"></el-option>
        </el-select>
      </div>
      <div class="flex-center">
        <el-button
          class="btn-add small"
          size="small"
          icon="el-icon-plus"
          @click="openCreateModal"
        >
          {{ $t("happilyVillage.Create_Campaign") }}
        </el-button>
      </div>
    </div>
    
    <div class="mt-12">
      <div class="university-table-wrapper">
        <el-table
          class="table-happily-village campaigns-table"
          :data="campaigns"
          style="width: 100%"
          :empty-text="loadingCampaigns ? ' ' : $t('happilyVillage.no_campaigns')"
        >
          <el-table-column
            :label="$t('happilyVillage.campaign_form.title')"
            width="280"
            prop="title"
          >
            <template slot-scope="scope">
              <div class="flex-center" style="gap: 10px;">
                <img
                  v-if="scope.row.iconKey"
                  :src="getIconPath(scope.row.iconKey)"
                  style="width: 28px; height: 28px; flex-shrink: 0;"
                  alt="icon"
                />
                <span class="campaign-title-link" @click="handleTitleClick(scope.row)">
                  {{ getDisplayText(scope.row.title) }}
                </span>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.Type')"
            width="90"
            prop="inputType"
          >
            <template slot-scope="scope">
              <span class="normal">{{ formatInputType(scope.row.inputType) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.period')"
            width="180"
          >
            <template slot-scope="scope">
              <div class="normal">
                {{ formatPeriod(scope.row.timing.startDate, scope.row.timing.endDate) }}
              </div>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.Reward')"
            width="130"
          >
            <template slot-scope="scope">
              <span class="normal">{{ formatReward(scope.row.reward) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.Entry_Cost')"
            width="100"
          >
            <template slot-scope="scope">
              <div class="flex-center" style="gap: 4px;">
                <div class="coin relative" style="width: 16px; height: 16px;"></div>
                <span class="normal">{{ scope.row.entryCost || 0 }}</span>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.Status')"
            width="100"
          >
            <template slot-scope="scope">
              <span :class="`status-badge status-${getCampaignStatus(scope.row)}`">
                {{ formatStatus(getCampaignStatus(scope.row)) }}
              </span>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.Participants')"
            width="120"
          >
            <template slot-scope="scope">
              <span 
                class="participant-link" 
                @click="openLeaderboard(scope.row)"
              >
                {{ getParticipantCount(scope.row.id) }}
              </span>
            </template>
          </el-table-column>
          
          <el-table-column
            :label="$t('happilyVillage.Actions')"
            width="90"
            align="center"
          >
            <template slot-scope="scope">
              <div class="campaign-actions-cell">
                <el-dropdown
                  trigger="click"
                  placement="bottom-end"
                  @command="handleActionCommand($event, scope.row)"
                >
                  <button
                    type="button"
                    class="campaign-actions-trigger"
                    :title="$t('happilyVillage.campaign_actions')"
                  >
                    <i class="el-icon-more"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="summary">{{ $t("happilyVillage.Summary") }}</el-dropdown-item>
                    <el-dropdown-item command="edit">{{ $t("happilyVillage.Edit") }}</el-dropdown-item>
                    <el-dropdown-item command="duplicate">{{ $t("happilyVillage.Duplicate") }}</el-dropdown-item>
                    <el-dropdown-item command="archive">{{ $t("happilyVillage.archive") }}</el-dropdown-item>
                    <el-dropdown-item
                      command="delete"
                      :disabled="!canDeleteCampaign(scope.row)"
                      divided
                    >
                      <span :class="{ 'campaign-action-delete-label': canDeleteCampaign(scope.row) }">
                        {{ canDeleteCampaign(scope.row) ? $t("happilyVillage.Delete") : $t("happilyVillage.delete_disabled_hint") }}
                      </span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <div class="center mt-20 mb-40 pt-20 pb-40" v-if="loadingCampaigns">
        <loading-even></loading-even>
      </div>
      
      <div class="flex-center pagination mt-10" v-if="!loadingCampaigns && campaigns.length > 0">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="10"
          :pager-count="5"
          layout="prev, pager, next"
          :total="campaignsCount"
        ></el-pagination>
        <span class="results-status">
          {{ campaigns.length }} {{ $t("happilyVillage.results_from") }}
          {{ $utils("addCommasInNumber", campaignsCount) }} {{ $t("happilyVillage.results") }}
        </span>
      </div>
    </div>
    
    <!-- Campaign Form Modal -->
    <university-campaign-form
      :visible="campaignModalVisible"
      :submitting="submittingCampaign"
      :campaign="selectedCampaign"
      @close="closeCampaignModal"
      @submit="handleCampaignSubmit"
    />
    
    <!-- Leaderboard Modal -->
    <campaign-leaderboard-modal
      :visible="leaderboardModalVisible"
      :campaign="leaderboardCampaign"
      @close="closeLeaderboardModal"
    />

    <!-- Summary Modal -->
    <campaign-summary-modal
      :visible="summaryModalVisible"
      :campaign="summaryCampaign"
      @close="closeSummaryModal"
      @open-leaderboard="handleSummaryOpenLeaderboard"
    />
  </div>
</template>

<script>
import format from "date-fns/format";
import _ from "lodash";

export default {
  name: "university-campaigns",
  components: {
    loadingEven: require("../even/loading").default,
    universityCampaignForm: require("./university-campaign-form").default,
    campaignLeaderboardModal: require("./CampaignLeaderboardModal").default,
    campaignSummaryModal: require("./CampaignSummaryModal").default,
  },
  data() {
    return {
      searchQuery: "",
      statusFilter: "all",
      currentPage: 1,
      campaignModalVisible: false,
      submittingCampaign: false,
      selectedCampaign: null,
      leaderboardModalVisible: false,
      leaderboardCampaign: null,
      summaryModalVisible: false,
      summaryCampaign: null,
    };
  },
  computed: {
    allCampaigns() {
      return this.$store.getters["happilyvillageUniversity/campaigns"];
    },
    campaigns() {
      let filtered = this.allCampaigns;
      
      // Filter by status
      if (this.statusFilter && this.statusFilter !== 'all') {
        filtered = filtered.filter(campaign => {
          const status = this.getCampaignStatus(campaign);
          return status === this.statusFilter;
        });
      }
      
      // Filter by search query
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(campaign => {
          const title = this.getDisplayText(campaign.title, '').toLowerCase();
          return title.includes(query);
        });
      }
      
      return filtered;
    },
    campaignsCount() {
      return this.campaigns.length;
    },
    totalCampaignsCount() {
      return this.allCampaigns.length;
    },
    loadingCampaigns() {
      return this.$store.getters["happilyvillageUniversity/loadingCampaigns"];
    },
    campaignParticipantCounts() {
      return this.$store.getters["happilyvillageUniversity/campaignParticipantCounts"];
    },
  },
  async created() {
    await this.$store.dispatch("happilyvillageUniversity/fetchCampaigns");
    this.$store.dispatch("happilyvillageUniversity/fetchCampaignParticipantCounts");
  },
  methods: {
    // Helper to extract text from multilingual objects
    getDisplayText(field, fallback = '') {
      if (!field) return fallback;
      // If it's a string (old format), return directly
      if (typeof field === 'string') return field || fallback;
      // If it's an object (multilingual format), prefer English then any available
      if (typeof field === 'object') {
        return field.en || field.th || Object.values(field).find(v => v) || fallback;
      }
      return fallback;
    },
    handleSearch() {
      // Filtering is reactive via computed property
      // Reset to first page when searching
      this.currentPage = 1;
    },
    handleFilterChange() {
      // Filtering is reactive via computed property
      // Reset to first page when filter changes
      this.currentPage = 1;
    },
    getCampaignStatus(campaign) {
      // Stored statuses take precedence.
      if (campaign.status === 'archived') return 'archived';
      // 'scheduled' = sign-ups not yet open. Backend cron flips to 'active'
      // when launchAt passes.
      if (campaign.status === 'scheduled') return 'scheduled';
      // 'completed' = backend cron flagged the campaign as past its endDate.
      if (campaign.status === 'completed') return 'ended';

      // For active or unknown-status campaigns, fall back to comparing
      // endDate (delivery end) to now.
      const endDate = campaign.timing ? this.parseDate(campaign.timing.endDate) : null;
      if (endDate && new Date() > endDate) return 'ended';

      return campaign.status || 'active';
    },
    formatStatus(status) {
      if (!status) return '';
      const labels = {
        active: this.$t("happilyVillage.status_active"),
        scheduled: this.$t("happilyVillage.status_scheduled"),
        ended: this.$t("happilyVillage.status_ended"),
        archived: this.$t("happilyVillage.status_archived"),
      };
      return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
    },
    handlePageChange(page) {
      this.currentPage = page;
      // TODO(backend): Fetch page from backend
    },
    handleActionCommand(command, campaign) {
      switch (command) {
        case "summary":
          this.openSummary(campaign);
          return;
        case "edit":
          this.editCampaign(campaign);
          return;
        case "duplicate":
          this.duplicateCampaign(campaign);
          return;
        case "archive":
          this.archiveCampaign(campaign);
          return;
        case "delete":
          this.deleteCampaign(campaign);
          return;
        default:
          return;
      }
    },
    openCreateModal() {
      this.submittingCampaign = false;
      this.selectedCampaign = null;
      this.campaignModalVisible = true;
    },
    appendCopySuffix(value) {
      const trimmedValue = (value || "").trim();
      if (!trimmedValue) {
        return "Copy";
      }
      return trimmedValue.endsWith(" (Copy)")
        ? trimmedValue
        : `${trimmedValue} (Copy)`;
    },
    cloneValue(value) {
      if (value === undefined || value === null) {
        return value;
      }
      return _.cloneDeep(value);
    },
    cloneMultilingualField(field, englishFallback = "", { appendCopyToEnglish = false } = {}) {
      if (field && typeof field === "object" && !Array.isArray(field)) {
        const clonedField = this.cloneValue(field);
        const englishValue = clonedField.en
          || englishFallback
          || Object.values(clonedField).find(value => typeof value === "string" && value.trim())
          || "";
        clonedField.en = appendCopyToEnglish
          ? this.appendCopySuffix(englishValue)
          : englishValue;
        return clonedField;
      }

      const englishValue = englishFallback || (typeof field === "string" ? field : "");
      const clonedField = {
        en: appendCopyToEnglish
          ? this.appendCopySuffix(englishValue)
          : englishValue,
      };

      if (typeof field === "string" && field && field !== englishValue) {
        clonedField.th = field;
      }

      return clonedField;
    },
    getDefaultTimingForDuplicate(campaign) {
      const frequency = campaign && campaign.timing && campaign.timing.frequency
        ? campaign.timing.frequency
        : "daily";

      return {
        frequency,
        startDate: null,
        endDate: null,
        occurrences: (campaign && campaign.timing && campaign.timing.occurrences)
          || (frequency === "weekly" ? 8 : 30),
        dayOfWeek: campaign && campaign.timing && campaign.timing.dayOfWeek !== undefined
          ? campaign.timing.dayOfWeek
          : 1,
      };
    },
    sanitizeCampaignForDuplicate(campaign) {
      const yesNoSuccessAnswer = campaign && campaign.yesNoSuccessAnswer
        ? campaign.yesNoSuccessAnswer
        : "yes";

      return {
        campaignCategory: this.cloneMultilingualField(
          campaign && campaign.campaignCategory,
          campaign && campaign.campaignCategory_en
        ),
        title: this.cloneMultilingualField(
          campaign && campaign.title,
          campaign && campaign.title_en,
          { appendCopyToEnglish: true }
        ),
        description: this.cloneMultilingualField(
          campaign && campaign.description,
          campaign && campaign.description_en
        ),
        iconKey: (campaign && campaign.iconKey) || null,
        theme: this.cloneValue((campaign && campaign.theme) || { gradientKey: "purple", accentHex: "#7D5ABA" }),
        inputType: (campaign && campaign.inputType) || "count",
        inputSubType: (campaign && campaign.inputSubType) || null,
        yesNoSuccessAnswer,
        yesNoOptions: this.cloneValue(campaign && campaign.yesNoOptions)
          || (yesNoSuccessAnswer === "no"
            ? { yes: { en: "Yes", th: "ใช่" }, no: { en: "No, I didn't!", th: "ไม่ ยังไม่ได้ทำ!" } }
            : { yes: { en: "Yes, I did!", th: "ใช่ ทำแล้ว!" }, no: { en: "No", th: "ไม่" } }),
        allowDecimals: !!(campaign && campaign.allowDecimals),
        trackingMode: (campaign && campaign.trackingMode) || "incremental",
        timing: this.getDefaultTimingForDuplicate(campaign),
        goals: {
          dailyEnabled: true,
          dailyTarget: (campaign && campaign.goals && campaign.goals.dailyTarget) || 10,
          individualEnabled: true,
          individualTarget: (campaign && campaign.goals && campaign.goals.individualTarget) || 240,
          groupEnabled: !!(campaign && campaign.goals && campaign.goals.groupEnabled),
          groupTarget: (campaign && campaign.goals && campaign.goals.groupTarget) || 1000,
          unit: this.cloneMultilingualField(
            campaign && campaign.goals && campaign.goals.unit,
            campaign && campaign.goals && campaign.goals.unit_en
          ),
        },
        reward: {
          type: (campaign && campaign.reward && campaign.reward.type) || "charged",
          coins: (campaign && campaign.reward && campaign.reward.coins) || 1,
          chargedCoins: (campaign && campaign.reward && campaign.reward.chargedCoins) || 1,
          prizePoolCoins: (campaign && campaign.reward && campaign.reward.prizePoolCoins) || 1,
          multipleSelectionRule: (campaign && campaign.reward && campaign.reward.multipleSelectionRule) || "per_item",
        },
        entryCost: campaign && campaign.entryCost !== undefined ? campaign.entryCost : 5,
        entryLimit: (campaign && campaign.entryLimit) || 100,
        entryLimitEnabled: !!(campaign && campaign.entryLimitEnabled),
        commentPermission: campaign && campaign.commentPermission !== undefined
          ? campaign.commentPermission
          : ((campaign && campaign.comment_type) || 1),
        quizQuestions: this.cloneValue((campaign && campaign.quizQuestions) || []),
        checklistItems: this.cloneValue((campaign && campaign.checklistItems) || []),
        huddles: this.cloneValue(campaign && campaign.huddles),
      };
    },
    duplicateCampaign(campaign) {
      this.submittingCampaign = false;
      this.selectedCampaign = this.sanitizeCampaignForDuplicate(campaign);
      this.campaignModalVisible = true;
    },
    editCampaign(campaign) {
      this.submittingCampaign = false;
      this.selectedCampaign = { ...campaign };
      this.campaignModalVisible = true;
    },
    handleTitleClick(campaign) {
      const status = this.getCampaignStatus(campaign);
      if (status === "ended") {
        this.openSummary(campaign);
      } else if (this.hasCampaignStarted(campaign)) {
        this.openLeaderboard(campaign);
      } else {
        this.editCampaign(campaign);
      }
    },
    hasCampaignStarted(campaign) {
      if (!campaign || !campaign.timing || !campaign.timing.startDate) return false;
      
      const startDate = this.parseDate(campaign.timing.startDate);
      if (!startDate) return false;
      
      const now = new Date();
      return now >= startDate;
    },
    parseDate(dateValue) {
      if (!dateValue) return null;
      if (dateValue.toDate) return dateValue.toDate();
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === 'string') return new Date(dateValue);
      return null;
    },
    archiveCampaign(campaign) {
      this.$confirm(
        "This will archive the campaign. Continue?",
        "Warning",
        {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      ).then(() => {
        // Archive campaign in Firestore
        this.$store.dispatch("happilyvillageUniversity/archiveCampaign", campaign.id)
          .then(() => {
            this.$message({
              type: "success",
              message: "Campaign archived",
            });
          })
          .catch((error) => {
            this.$message({
              type: "error",
              message: `Failed to archive: ${error.message}`,
            });
          });
      });
    },
    getKnownParticipantCount(campaign) {
      if (!campaign || !campaign.id) return null;

      const liveCount = this.campaignParticipantCounts[campaign.id];
      if (liveCount !== undefined) return liveCount;

      if (campaign.totalParticipants !== undefined && campaign.totalParticipants !== null) {
        return campaign.totalParticipants;
      }

      return null;
    },
    canDeleteCampaign(campaign) {
      return this.getKnownParticipantCount(campaign) === 0;
    },
    deleteCampaign(campaign) {
      const participantCount = this.getKnownParticipantCount(campaign);

      if (participantCount !== 0) {
        this.$message({
          type: "warning",
          message: "Only campaigns with no participants can be permanently deleted. Archive this one instead.",
        });
        return;
      }

      this.$confirm(
        "This will permanently delete the campaign. This cannot be undone.",
        "Delete Campaign?",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      ).then(() => {
        this.$store.dispatch("happilyvillageUniversity/deleteCampaign", campaign.id)
          .then(() => {
            if (this.summaryCampaign && this.summaryCampaign.id === campaign.id) {
              this.closeSummaryModal();
            }
            if (this.leaderboardCampaign && this.leaderboardCampaign.id === campaign.id) {
              this.closeLeaderboardModal();
            }
            if (this.selectedCampaign && this.selectedCampaign.id === campaign.id) {
              this.closeCampaignModal();
            }
            this.$message({
              type: "success",
              message: "Campaign deleted permanently",
            });
          })
          .catch((error) => {
            this.$message({
              type: "error",
              message: `Failed to delete: ${error.message}`,
            });
          });
      });
    },
    closeCampaignModal() {
      this.submittingCampaign = false;
      this.campaignModalVisible = false;
      this.selectedCampaign = null;
    },
    async handleCampaignSubmit(data) {
      if (this.submittingCampaign) {
        return;
      }

      this.submittingCampaign = true;

      // data is now { form, huddles } - same pattern as Town Hall and University Sessions
      const { form, huddles } = data;
      
      try {
        if (this.selectedCampaign && this.selectedCampaign.id) {
          // Edit existing campaign
          await this.$store.dispatch("happilyvillageUniversity/updateCampaign", {
            id: this.selectedCampaign.id,
            form,
            huddles,
          });
          this.$message({
            type: "success",
            message: "Campaign updated successfully",
          });
        } else {
          // Create new campaign - pass { form, huddles } object
          await this.$store.dispatch("happilyvillageUniversity/createCampaign", { form, huddles });
          this.$message({
            type: "success",
            message: "Campaign created successfully",
          });
        }
        this.closeCampaignModal();
      } catch (error) {
        this.$message({
          type: "error",
          message: `Failed to ${this.selectedCampaign && this.selectedCampaign.id ? "update" : "create"}: ${error.message}`,
        });
      } finally {
        this.submittingCampaign = false;
      }
    },
    getIconPath(iconKey) {
      // Check if iconKey is a full URL (new format)
      if (iconKey && iconKey.startsWith('http')) {
        return iconKey;
      }

      // Legacy format: icon key that requires local asset
      try {
        return require(`@/assets/icons/${iconKey}.png`);
      } catch {
        return require("@/assets/icons/ic_star_24px.png");
      }
    },
    formatInputType(type) {
      const types = {
        quiz: this.$t("happilyVillage.type_quiz"),
        count: this.$t("happilyVillage.type_count"),
        auto_sync: this.$t("happilyVillage.type_auto_sync"),
        auto_sync_steps: this.$t("happilyVillage.type_steps"),
        auto_sync_sleep: this.$t("happilyVillage.type_sleep"),
        auto_sync_screen: this.$t("happilyVillage.type_screen_time"),
        auto_sync_exercise: this.$t("happilyVillage.type_exercise"),
        auto_sync_distance: this.$t("happilyVillage.type_distance"),
        multiple_selection: this.$t("happilyVillage.type_multiple_selection"),
        yes_no: this.$t("happilyVillage.type_yes_no"),
      };
      return types[type] || type;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return format(new Date(dateStr), "MMM D, YYYY");
      } catch {
        return dateStr;
      }
    },
    formatPeriod(startDateStr, endDateStr) {
      if (!startDateStr || !endDateStr) return "";
      try {
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        
        if (startYear === endYear) {
          // Same year: "Dec 10 - Jan 20"
          return `${format(startDate, "MMM D")} - ${format(endDate, "MMM D")}`;
        } else {
          // Different years: "Dec 10, 2025 - Jan 20, 2026"
          return `${format(startDate, "MMM D, YYYY")} - ${format(endDate, "MMM D, YYYY")}`;
        }
      } catch {
        return `${startDateStr} - ${endDateStr}`;
      }
    },
    formatReward(reward) {
      if (!reward) return "-";
      const types = {
        instant: this.$t("happilyVillage.Instant_Coins"),
        charged: this.$t("happilyVillage.Charged_Coins_On_Completion"),
        prize_pool: this.$t("happilyVillage.Prize_Pool_Split_At_End"),
      };
      return types[reward.type] || reward.type;
    },
    getParticipantCount(campaignId) {
      if (!campaignId) return 0;

      const liveCount = this.campaignParticipantCounts[campaignId];
      if (liveCount !== undefined) return liveCount;

      const campaign = this.allCampaigns.find(c => c.id === campaignId);
      if (campaign && campaign.totalParticipants > 0) return campaign.totalParticipants;

      return 0;
    },
    openLeaderboard(campaign) {
      this.leaderboardCampaign = { ...campaign };
      this.leaderboardModalVisible = true;
    },
    closeLeaderboardModal() {
      this.leaderboardModalVisible = false;
      this.leaderboardCampaign = null;
    },
    openSummary(campaign) {
      this.summaryCampaign = { ...campaign };
      this.summaryModalVisible = true;
    },
    closeSummaryModal() {
      this.summaryModalVisible = false;
      this.summaryCampaign = null;
    },
    handleSummaryOpenLeaderboard(campaign) {
      this.closeSummaryModal();
      this.$nextTick(() => {
        this.openLeaderboard(campaign);
      });
    },
  },
};
</script>

<style scoped>
#universityCampaigns {
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
}

.sub-title {
  margin-top: 24px;
  margin-bottom: 16px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-active {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.status-upcoming {
  background-color: #E3F2FD;
  color: #1976D2;
}

.status-scheduled {
  background-color: #E3F2FD;
  color: #1976D2;
}

.status-ended {
  background-color: #F5F5F5;
  color: #757575;
}

.status-draft {
  background-color: #FFF3E0;
  color: #E65100;
}

.status-archived {
  background-color: #FAFAFA;
  color: #9E9E9E;
}

.coin {
  background-image: url("~@/assets/images/ic_coin.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* Action Menu */
.campaign-actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.campaign-actions-trigger {
  width: 32px;
  height: 32px;
  border: 1px solid #E5E7EB;
  border-radius: 999px;
  background: #FFFFFF;
  color: #4B5563;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.campaign-actions-trigger:hover,
.campaign-actions-trigger:focus {
  background: #F9FAFB;
  border-color: #D1D5DB;
  color: #111827;
  outline: none;
}

.campaign-actions-trigger i {
  font-size: 16px;
  font-weight: 700;
}

.campaign-action-delete-label {
  color: #B42318;
}

/* Campaign Title Link */
.campaign-title-link {
  font-weight: 700;
  font-size: 14px;
  line-height: 1.3;
  color: #111827;
  cursor: pointer;
  transition: color 0.15s ease;
}

.campaign-title-link:hover {
  color: #6C30CC;
}

/* Participant Link */
.participant-link {
  color: #6C30CC;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.participant-link:hover {
  color: #5A28A8;
  text-decoration: underline;
}
</style>
